import { useRef, useState } from 'react';
import { PageHeader } from 'components/ui';
import { ProductGrid } from 'components/ProductGrid';
import { useToast } from 'components/toast/ToastProvider';
import { generateClientRequestId } from 'api/graphql/inventory';
import { useVendorCatalog } from 'features/catalog/hooks/useVendorCatalog';
import { useInventoryAdjustment, useAdjustmentReasons } from '../hooks/useInventoryAdjustment';
import { AdjustStockModal } from '../components/AdjustStockModal';

/** Stock adjustment: search/grid → pick a product → adjust its stock. */
export function InventoryAdjustmentPage() {
  const { items: catalog, loading, error, refetch } = useVendorCatalog();
  const { reasons } = useAdjustmentReasons();
  const { recordAdjustment, loading: saving } = useInventoryAdjustment();
  const toast = useToast();

  const [selected, setSelected] = useState(null);
  const requestIdRef = useRef(generateClientRequestId());

  const handleSubmit = async ({ quantityDelta, reason, notes }) => {
    try {
      const payload = await recordAdjustment({
        vendorCatalogId: selected.vendorCatalogId,
        quantityDelta,
        reason,
        notes,
        clientRequestId: requestIdRef.current,
      });
      if (payload?.success) {
        toast.success(
          'Stock adjusted',
          payload.message || `${selected.vendorProductName || selected.name || 'Product'} updated.`,
        );
        setSelected(null);
        requestIdRef.current = generateClientRequestId();
      } else {
        toast.error('Could not adjust stock', payload?.message || 'Please try again.');
      }
    } catch (err) {
      toast.error('Something went wrong', err.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Stock adjustment"
        description="Search a product, then correct its stock for damage, expiry, recounts and more."
      />

      <ProductGrid
        items={catalog}
        loading={loading}
        error={error}
        onRetry={refetch}
        onSelect={setSelected}
      />

      <AdjustStockModal
        key={selected?.vendorCatalogId}
        product={selected}
        reasons={reasons}
        saving={saving}
        onSubmit={handleSubmit}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

export default InventoryAdjustmentPage;
